'use client'

import React, { ReactNode, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

interface StickyDesignSectionProps {
  children: ReactNode
  scrollContainerRef: RefObject<HTMLElement | null>
  disabled?: boolean
  stickyOffset?: number
  snapThreshold?: number
}

interface ScrollMetrics {
  start: number
  range: number
}

interface CrossedBoundaries {
  start: boolean
  end: boolean
}

const DEFAULT_STICKY_OFFSET = 80
const DEFAULT_SNAP_THRESHOLD = 70
const SCROLL_TOLERANCE = 1
const SNAP_DELAY = 120

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const StickyDesignSection: React.FC<StickyDesignSectionProps> = ({
  children,
  scrollContainerRef,
  disabled = false,
  stickyOffset = DEFAULT_STICKY_OFFSET,
  snapThreshold = DEFAULT_SNAP_THRESHOLD,
}) => {
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const metricsRef = useRef<ScrollMetrics>({ start: 0, range: 0 })
  const expectedPanelScrollRef = useRef<number | null>(null)
  const expectedWindowScrollRef = useRef<number | null>(null)
  const lastWindowScrollRef = useRef<number | null>(null)
  const scrollDirectionRef = useRef<'up' | 'down' | null>(null)
  const crossedBoundariesRef = useRef<CrossedBoundaries>({ start: false, end: false })
  const [trackHeight, setTrackHeight] = useState<number | null>(null)

  const updateMetrics = useCallback(() => {
    const track = trackRef.current
    const stage = stageRef.current
    const scrollContainer = scrollContainerRef.current

    if (!track || !stage || !scrollContainer || disabled) return

    const stageHeight = stage.getBoundingClientRect().height
    const scrollRange = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight)
    const nextTrackHeight = Math.ceil(stageHeight + scrollRange)
    const trackTop = track.getBoundingClientRect().top + window.scrollY

    metricsRef.current = {
      start: trackTop - stickyOffset,
      range: scrollRange,
    }

    setTrackHeight((currentHeight) => (currentHeight === nextTrackHeight ? currentHeight : nextTrackHeight))
  }, [disabled, scrollContainerRef, stickyOffset])

  const syncPanelFromWindow = useCallback(() => {
    if (disabled) return

    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const { start, range } = metricsRef.current
    const nextScrollTop = clamp(window.scrollY - start, 0, range)

    if (Math.abs(scrollContainer.scrollTop - nextScrollTop) <= SCROLL_TOLERANCE) return

    expectedPanelScrollRef.current = nextScrollTop
    scrollContainer.scrollTop = nextScrollTop
  }, [disabled, scrollContainerRef])

  const syncWindowFromPanel = useCallback(() => {
    if (disabled) return

    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const { start, range } = metricsRef.current
    const nextWindowScroll = start + clamp(scrollContainer.scrollTop, 0, range)

    if (Math.abs(window.scrollY - nextWindowScroll) <= SCROLL_TOLERANCE) return

    expectedWindowScrollRef.current = nextWindowScroll
    window.scrollTo({ top: nextWindowScroll, behavior: 'auto' })
  }, [disabled, scrollContainerRef])

  useLayoutEffect(() => {
    if (disabled) return

    const scrollContainer = scrollContainerRef.current
    const stage = stageRef.current
    if (!scrollContainer || !stage) return

    let measureFrame = 0
    const scheduleMeasurement = () => {
      cancelAnimationFrame(measureFrame)
      measureFrame = requestAnimationFrame(() => {
        updateMetrics()
        syncPanelFromWindow()
      })
    }

    const resizeObserver = new ResizeObserver(scheduleMeasurement)
    const observePanelChildren = () => {
      Array.from(scrollContainer.children).forEach((child) => resizeObserver.observe(child))
    }

    resizeObserver.observe(stage)
    resizeObserver.observe(scrollContainer)
    observePanelChildren()

    const mutationObserver = new MutationObserver(() => {
      observePanelChildren()
      scheduleMeasurement()
    })
    mutationObserver.observe(scrollContainer, { childList: true, subtree: true })

    window.addEventListener('resize', scheduleMeasurement)
    scheduleMeasurement()

    return () => {
      cancelAnimationFrame(measureFrame)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener('resize', scheduleMeasurement)
    }
  }, [disabled, scrollContainerRef, syncPanelFromWindow, updateMetrics])

  useEffect(() => {
    if (disabled) return

    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    let windowFrame = 0
    let panelFrame = 0
    let snapTimer = 0

    const snapToBoundary = () => {
      const { start, range } = metricsRef.current
      const end = start + range
      const currentScroll = window.scrollY
      const direction = scrollDirectionRef.current
      const crossedBoundaries = crossedBoundariesRef.current
      const candidates = [
        {
          position: start,
          crossed: crossedBoundaries.start,
          approaching: (direction === 'down' && currentScroll < start) || (direction === 'up' && currentScroll > start),
        },
        ...(range > SCROLL_TOLERANCE
          ? [
              {
                position: end,
                crossed: crossedBoundaries.end,
                approaching:
                  (direction === 'down' && currentScroll < end) || (direction === 'up' && currentScroll > end),
              },
            ]
          : []),
      ]
        .map((candidate) => ({ ...candidate, distance: Math.abs(currentScroll - candidate.position) }))
        .filter(
          (candidate) =>
            (candidate.approaching || candidate.crossed) &&
            candidate.distance > SCROLL_TOLERANCE &&
            candidate.distance <= snapThreshold
        )
        .sort((first, second) => first.distance - second.distance)

      crossedBoundariesRef.current = { start: false, end: false }

      const closestBoundary = candidates[0]
      if (!closestBoundary) return

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      window.scrollTo({ top: closestBoundary.position, behavior: prefersReducedMotion ? 'auto' : 'smooth' })
    }

    const scheduleSnap = () => {
      window.clearTimeout(snapTimer)
      snapTimer = window.setTimeout(snapToBoundary, SNAP_DELAY)
    }

    const handleWindowScroll = () => {
      const previousScroll = lastWindowScrollRef.current
      if (previousScroll !== null && window.scrollY !== previousScroll) {
        const { start, range } = metricsRef.current
        const end = start + range
        scrollDirectionRef.current = window.scrollY > previousScroll ? 'down' : 'up'
        crossedBoundariesRef.current.start ||=
          (previousScroll < start && window.scrollY >= start) || (previousScroll > start && window.scrollY <= start)
        crossedBoundariesRef.current.end ||=
          range > SCROLL_TOLERANCE &&
          ((previousScroll < end && window.scrollY >= end) || (previousScroll > end && window.scrollY <= end))
      }
      lastWindowScrollRef.current = window.scrollY
      scheduleSnap()

      const expectedScroll = expectedWindowScrollRef.current
      if (expectedScroll !== null && Math.abs(window.scrollY - expectedScroll) <= SCROLL_TOLERANCE) {
        expectedWindowScrollRef.current = null
        return
      }

      expectedWindowScrollRef.current = null
      cancelAnimationFrame(windowFrame)
      windowFrame = requestAnimationFrame(syncPanelFromWindow)
    }

    const handlePanelScroll = () => {
      const expectedScroll = expectedPanelScrollRef.current
      if (expectedScroll !== null && Math.abs(scrollContainer.scrollTop - expectedScroll) <= SCROLL_TOLERANCE) {
        expectedPanelScrollRef.current = null
        return
      }

      expectedPanelScrollRef.current = null
      cancelAnimationFrame(panelFrame)
      panelFrame = requestAnimationFrame(syncWindowFromPanel)
    }

    window.addEventListener('scroll', handleWindowScroll, { passive: true })
    scrollContainer.addEventListener('scroll', handlePanelScroll, { passive: true })
    lastWindowScrollRef.current = window.scrollY
    syncPanelFromWindow()

    return () => {
      cancelAnimationFrame(windowFrame)
      cancelAnimationFrame(panelFrame)
      window.clearTimeout(snapTimer)
      window.removeEventListener('scroll', handleWindowScroll)
      scrollContainer.removeEventListener('scroll', handlePanelScroll)
    }
  }, [disabled, scrollContainerRef, snapThreshold, syncPanelFromWindow, syncWindowFromPanel])

  return (
    <div
      ref={trackRef}
      className="relative w-full"
      style={{
        height: trackHeight ? `${trackHeight}px` : undefined,
      }}
    >
      <div
        ref={stageRef}
        className={disabled ? 'relative w-full' : 'sticky z-[10] w-full'}
        style={{ top: disabled ? undefined : `${stickyOffset}px` }}
      >
        {children}
      </div>
    </div>
  )
}

export default StickyDesignSection
